/**
 * MarkPaidModal.js — Record payment(s) against an approved payslip
 *
 * Opened from PayslipDetailModal when the manager clicks "Mark Paid" on an
 * approved payslip. Allows the manager to:
 *   1. Attribute existing unlinked payout records (advance payments already
 *      recorded earlier in the month) to this payslip.
 *   2. Add new payout rows inline (date, mode, amount).
 *
 * A live running total and difference from net_due helps the manager confirm
 * the correct amounts before submitting.
 *
 * Props:
 *   payslip  {object}   — full payslip object (needs id, net_due, worker)
 *   worker   {object}   — worker object (id, name)
 *   onClose  {()=>void} — dismiss without saving
 *   onSaved  {()=>void} — called on successful mark-paid
 */

import React, { useState, useEffect } from 'react';
import { Modal, showAlert } from './UI';
import { formatCurrency, formatDate } from '../utils/helpers';
import { getUnlinkedPayouts, markPayslipPaid, getSettings } from '../api/directus';

const MODES = ['upi', 'bank', 'cash'];

const today = () => new Date().toISOString().slice(0, 10);

export default function MarkPaidModal({ payslip, worker, onClose, onSaved }) {
  const [unlinked,     setUnlinked]     = useState([]);
  const [loadingExist, setLoadingExist] = useState(true);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [newRows,      setNewRows]      = useState([]);
  const [saving,       setSaving]       = useState(false);
  // Default 0 so the button stays disabled until settings load, unless netDue is already 0
  const [autoPayThreshold, setAutoPayThreshold] = useState(0);

  // Fetch existing unlinked payouts and the autoPay threshold in parallel on mount
  useEffect(() => {
    if (!worker?.id) return;
    getUnlinkedPayouts(worker.id)
      .then(r => setUnlinked(r.data || []))
      .catch(() => setUnlinked([]))
      .finally(() => setLoadingExist(false));
    getSettings()
      .then(r => setAutoPayThreshold(parseFloat(r.data?.payslip_autoPay_threshold) || 0))
      .catch(() => {});
  }, [worker?.id]);

  // ── Derived totals ────────────────────────────────────────────────────────

  const selectedTotal = unlinked
    .filter(p => selectedIds.has(p.id))
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  const newRowsTotal = newRows
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const grandTotal  = selectedTotal + newRowsTotal;
  const netDue      = parseFloat(payslip.net_due) || 0;
  const difference  = netDue - grandTotal;          // positive = still owed, negative = overpaid

  // ── Checkbox toggle ───────────────────────────────────────────────────────

  function toggleId(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // ── New rows management ───────────────────────────────────────────────────

  function addRow() {
    setNewRows(r => [...r, { date: today(), mode: 'cash', amount: '' }]);
  }

  function updateRow(i, field, value) {
    setNewRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  }

  function removeRow(i) {
    setNewRows(r => r.filter((_, idx) => idx !== i));
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async function handleConfirm() {
    const validNewRows = newRows.filter(r => r.amount && parseFloat(r.amount) > 0);
    // Payslips at or below the autoPay threshold settle without a payment entry
    if (netDue > autoPayThreshold && selectedIds.size === 0 && validNewRows.length === 0) {
      showAlert('Add at least one payment before confirming.', 'warning');
      return;
    }
    setSaving(true);
    try {
      await markPayslipPaid(payslip.id, {
        existing_payout_ids: [...selectedIds],
        new_payouts: validNewRows.map(r => ({
          date:   r.date,
          mode:   r.mode,
          amount: parseFloat(r.amount),
        })),
      });
      onSaved();
    } catch (err) {
      showAlert(err?.errors?.[0]?.message || 'Failed to mark as paid.', 'error');
    } finally {
      setSaving(false);
    }
  }

  // Payslips at or below the autoPay threshold need no payment entry
  const hasAnything = netDue <= autoPayThreshold || selectedIds.size > 0 || newRows.some(r => parseFloat(r.amount) > 0);

  // ── Diff label helpers ────────────────────────────────────────────────────

  function diffClass() {
    if (Math.abs(difference) < 0.5) return 'mpm-diff clear';
    if (difference > 0)             return 'mpm-diff owed';
    return 'mpm-diff credit';
  }

  function diffLabel() {
    if (Math.abs(difference) < 0.5) return 'Fully settled';
    if (difference > 0)             return `${formatCurrency(difference)} still owed`;
    return `${formatCurrency(Math.abs(difference))} overpaid`;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Modal
      open
      onClose={onClose}
      title="Mark as Paid"
      size="modal-lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={saving || !hasAnything}
          >
            {saving ? 'Saving…' : 'Confirm Payment'}
          </button>
        </>
      }
    >
      {/* Net due */}
      <div className="mpm-net-due">
        <p className="mpm-net-due-label">Net Due for {worker?.name}</p>
        <p className="mpm-net-due-amount">{formatCurrency(netDue)}</p>
      </div>

      {/* Existing unlinked payouts */}
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-muted)', marginBottom: 6 }}>
        Existing Unlinked Payouts
      </p>
      {loadingExist ? (
        <p style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Loading…</p>
      ) : unlinked.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 10 }}>None found.</p>
      ) : (
        <div style={{ marginBottom: 12 }}>
          {unlinked.map(p => (
            <label key={p.id} className="mpm-payout-row" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedIds.has(p.id)}
                onChange={() => toggleId(p.id)}
              />
              <span>{formatDate(p.date)}</span>
              <span style={{ textTransform: 'capitalize', color: 'var(--fg-muted)' }}>{p.mode}</span>
              <span style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(p.amount)}</span>
            </label>
          ))}
        </div>
      )}

      {/* New payment rows */}
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-muted)', marginBottom: 6 }}>
        New Payments
      </p>
      {newRows.map((row, i) => (
        <div key={i} className="mpm-new-row">
          <input
            type="date"
            className="ap-input"
            value={row.date}
            onChange={e => updateRow(i, 'date', e.target.value)}
          />
          <select
            className="ap-input"
            value={row.mode}
            onChange={e => updateRow(i, 'mode', e.target.value)}
          >
            {MODES.map(m => (
              <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
            ))}
          </select>
          <input
            type="number"
            className="ap-input"
            placeholder="Amount"
            value={row.amount}
            min="0"
            step="1"
            onChange={e => updateRow(i, 'amount', e.target.value)}
          />
          <button
            className="icon-btn"
            aria-label="Remove row"
            onClick={() => removeRow(i)}
            style={{ color: 'var(--fg-muted)' }}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>
      ))}
      <button
        className="btn btn-ghost"
        style={{ marginTop: 8, fontSize: 13 }}
        onClick={addRow}
      >
        + Add row
      </button>

      {/* Totals strip */}
      <div style={{ borderTop: '1px solid var(--color-neutral-100)', marginTop: 14 }}>
        <div className="mpm-totals">
          <span>Total Payments</span>
          <span>{formatCurrency(grandTotal)}</span>
        </div>
        <p className={diffClass()}>{diffLabel()}</p>
      </div>
    </Modal>
  );
}
